/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable max-len */
import * as XLSX from 'xlsx';
import { DateTime } from 'luxon';

import { EssayExecutionAirResultParticles, ResultExcelParticles } from '../model/essayExecutionResultAir';
import { Variable, VariableTypeEnum, VariableUnit } from '../model/variable';

import { AppTypeEnum } from '../model/appType';
import { ArrayUtils } from './arrayUtils';
import { AttachmentThermalService } from '../services/attachmentThermal.service';
import { CalibrationResponsibleEnum, ExternalCalibrationValue } from '../model/calibration';
import { Client } from '../model/client';
import { DateUtils } from './dateUtils';
import { InternalEquipment, InternalEquipmentTypeEnum } from '../model/internalEquipment';
import { InternalEquipmentStatusEnum } from '../model/internalEquipmentStatus';
import { NumberUtils } from './numberUtils';
import { SensorData } from '../model/sensorData';
import { StringUtils } from './stringUtils';
import { TranslateService } from '@ngx-translate/core';
import { VariableTypeService } from '../services/variableType.service';
import { firstValueFrom } from 'rxjs';
import { getJsDateFromExcel } from 'excel-date-to-js';
import { InternalEquipmentCustomFieldsService } from '../services/internalEquipmentCustomFields.service';
import { InternalEquipmentCustomField } from '../model/internalEquipmentCustomField';
import { InternalEquipmentCustomFieldValue } from '../model/internalEquipmentCustomFieldValue';
import { InternalEquipmentFolderService } from '../services/internalEquipmentFolder.service';
import { InternalEquipmentFolder } from '../model/internalEquipmentFolder';
import { InternalEquipmentPerformances } from '../model/internalEquipmentPerformances';
import { CalibrationAmbientResultParticles, CalibrationResultExcelParticles } from '../model/calibrationAmbientConditions';
import { AttachmentQualificatesService } from '../services/attachmentQualificates.service';
import { EquipmentPerformances } from './equipmentUtils';
import { InternalEquipmentBalanceMass } from '../model/internalEquipmentBalanceMass';
import { AttachmentCalibratesService } from '../services/attachmentCalibrates.service';

const daysToSec = 24 * 60 * 60;

export interface ConvertedFile {
    file: File;
    raw: boolean;
}

const tryFormats = [
  'MM/dd/yyyy HH:mm:ss.SSS', // US
  'dd/MM/yyyy HH:mm:ss.SSS', // EU
  'd/M/yyyy HH:mm:ss.SSS',   // Short EU
  'MM/dd/yyyy HH:mm:ss',     // US sin milis
  'dd/MM/yyyy HH:mm:ss',
  'd/M/yyyy HH:mm:ss',
  'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ',  // ISO 8601
];

export class ExcelUtils {
    public static async excelToSensorData(
        file: File, attachmentThermalService: AttachmentThermalService | AttachmentCalibratesService, idVariable?: number, multiple?: boolean,
        totalSensors?: number, allColumns = false): Promise<SensorData[]> {
        const res = await this.convertToCsv(file, attachmentThermalService);
        const data = res.file;
        const raw = res.raw;
        const dataExcel = await this.loadExcel(data, raw);
        return this.loadSensorData(dataExcel, idVariable, multiple, totalSensors, allColumns);
    }

    public static async excelToSensorDataQualificates(
        file: File, attachmentQualificatesService: AttachmentQualificatesService, idVariable?: number, multiple?: boolean,
        totalSensors?: number): Promise<SensorData[]> {
        const res = await this.convertToCsvQualificates(file, attachmentQualificatesService);
        const data = res.file;
        const raw = res.raw;
        const dataExcel = await this.loadExcel(data, raw);
        return this.loadSensorData(dataExcel, idVariable, multiple, totalSensors);
    }

    public static async excelToInternalEquipment(file: File, variableTypeService: VariableTypeService, translate: TranslateService):
        Promise<InternalEquipment[]> {
        const data = await this.loadExcel(file, false);
        return this.loadInternalEquipment(data, variableTypeService, translate);
    }

    public static async excelToInternalEquipmentCustomField(file: File, variableTypeService: VariableTypeService,
        translate: TranslateService, internalEquipmentCustomFieldsService: InternalEquipmentCustomFieldsService,
         internalEquipmentFolderService: InternalEquipmentFolderService, isInternal: boolean):
    Promise<InternalEquipment[]> {
        const data = await this.loadExcelMoreSheet(file, false);
        return this.loadInternalEquipmentCustomField(data, variableTypeService, translate, internalEquipmentCustomFieldsService,
            internalEquipmentFolderService, isInternal);
    }

    public static async excelToParticleCount(file: File, attachmentThermalService: AttachmentThermalService):
        Promise<ResultExcelParticles> {
        const res = await this.convertToCsv(file, attachmentThermalService);
        const data = res.file;
        const raw = res.raw;
        const dataExcel = await this.loadExcel(data, raw);

        if (!dataExcel.some(lines => lines.length > 1)) {
            for (let i = 0; i < dataExcel.length; i++) {
                const line = dataExcel[i].toString();
                dataExcel[i] = line.split('.');
            }
        }

        return this.loadResultParticles(dataExcel);
    }

    public static async excelToClient(file: File): Promise<Client[]> {
        const data = await this.loadExcel(file, false);
        return this.loadClient(data);
    }

    public static async excelToCalibrationParticleCount(file: File, attachmentThermalService: AttachmentThermalService):
    Promise<CalibrationResultExcelParticles> {
        const res = await this.convertToCsv(file, attachmentThermalService);
        const data = res.file;
        const raw = res.raw;
        const dataExcel = await this.loadExcel(data, raw);

        if (!dataExcel.some(lines => lines.length > 1)) {
            for (let i = 0; i < dataExcel.length; i++) {
                const line = dataExcel[i].toString();
                dataExcel[i] = line.split('.');
            }
        }

        return this.loadResultParticlesCalibration(dataExcel);
    }

    public static async excelToBalanceMass(file: File): Promise<ExternalCalibrationValue[]> {
        const data = await this.loadExcel(file, false);
        return this.loadResultBalanceMass(data);
    }

    private static convertToCsv(file: File, attachmentThermalService: AttachmentThermalService | AttachmentCalibratesService): Promise<ConvertedFile> {
        return new Promise((resolve, reject) => {
            if (file.name == null || file.name.toLowerCase().endsWith('.csv') || file.name.toLowerCase().endsWith('.pdf')) {
                resolve({ file, raw: file.name.toLowerCase().endsWith('.csv') });
            } else {
                attachmentThermalService.convertExcelToCsv(file).subscribe((res: File) => resolve({ file: res, raw: true }),
                    err => reject(err));
            }

        });
    }

    private static convertToCsvQualificates(file: File, attachmentQualificatesService: AttachmentQualificatesService): Promise<ConvertedFile> {
        return new Promise((resolve, reject) => {
            if (file.name == null || file.name.toLowerCase().endsWith('.csv') || file.name.toLowerCase().endsWith('.pdf')) {
                resolve({ file, raw: file.name.toLowerCase().endsWith('.csv') });
            } else {
                attachmentQualificatesService.convertExcelToCsv(file).subscribe((res: File) => resolve({ file: res, raw: true }),
                    err => reject(err));
            }

        });
    }

    private static loadExcel(file: File, raw: boolean): Promise<any[][]> {
        return new Promise((resolve, reject) => {

            if (typeof file === 'string') {
                resolve(this.stringToWb(file, raw));
            } else {
                const reader: FileReader = new FileReader();

                reader.onload = (e: any) => resolve(this.stringToWb(e.target.result as string, raw));

                reader.onerror = (error: any) => reject(error);

                reader.readAsBinaryString(file);
            }
        });
    }

    private static stringToWb(str: string, raw: boolean): any[][] {
        let data: any[][];

        /* read workbook */
        const bstr: string = str;
        const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary', raw });

        /* grab first sheet */
        const wsname: string = wb.SheetNames[0];
        const ws: XLSX.WorkSheet = wb.Sheets[wsname];

        const options: XLSX.Sheet2JSONOpts = { header: 1, raw: true };

        /* save data */
        data = (XLSX.utils.sheet_to_json(ws, options));

        data = data.map((s: any[]) => {
            if (s.length === 1 && typeof s[0] === 'string') {
                const dat = s[0];
                s = dat.split(';');
            }

            return s as any[][];
        });

        return data;
    }

    private static async loadInternalEquipmentCustomField(data: Map<string, any[]>, variableTypeService: VariableTypeService,
        translate: TranslateService, internalEquipmentCustomFieldsService: InternalEquipmentCustomFieldsService,
        internalEquipmentFolderService: InternalEquipmentFolderService, isInternal: boolean):
        Promise<InternalEquipment[]> {
        let res: InternalEquipment[] = [];
        let errors: String[] = [];
        const mapVariables: Map<string, number> = new Map();
        const listVariables = await firstValueFrom(variableTypeService.findAll()) as Variable[];
        // eslint-disable-next-line max-len
        const listCustomFields = await firstValueFrom(internalEquipmentCustomFieldsService.findAll()).then(er => er?.content) as InternalEquipmentCustomField[];
        const listFolders = await firstValueFrom(internalEquipmentFolderService.getAll()) as InternalEquipmentFolder[];

        listVariables.forEach(v => mapVariables.set(translate.instant('variable.'.concat(v.translation)) as string, v.id));

        // Sheet Information
        const information = data.get(translate.instant('internalEq.sheetName.information') as string) as any[];
        for (let i = 1; i < information.length; i++) {
            const performanances: InternalEquipmentPerformances[] = [];

            const rowNum = i + 1;
            const row = information[i] as any;

            const eq = new InternalEquipment();
            eq.name = this.getDataFromCell(row, 0);
            eq.group = this.getDataFromCell(row, 1);
            eq.equipment = this.getDataFromCell(row, 2);
            eq.maker = this.getDataFromCell(row, 3);
            eq.model = this.getDataFromCell(row, 4);
            eq.serialNum = this.getDataFromCell(row, 5);
            const aDate = this.getDateFromCell(row, 6);
            if (aDate != null) {
                const resultaDate = DateUtils.anyToDateWitouhTime(aDate);
                eq.acquisitionDate = resultaDate;
            }

            const idApps: number[] = [];
            const applications = this.getDataFromCell(row, 7)?.toLowerCase().split(',').map(a => a.trim());
            if (!ArrayUtils.isEmpty(applications)) {
                applications.forEach(app => {
                    if (app === 'thermal') {
                        idApps.push(AppTypeEnum.THERMAL);
                    } else if (app === 'calibrates') {
                        idApps.push(AppTypeEnum.CALIBRATES);
                    } else if (app === 'air') {
                        idApps.push(AppTypeEnum.AIR);
                    }
                });
            }
            eq.idsApps = idApps;

            eq.location = this.getDataFromCell(row, 8);
            
            eq.internal = isInternal;
            const typesEq = this.getDataFromCell(row, 9);
            if (typesEq) {
                if (typesEq != null && typesEq.trim().toLowerCase() === 'equipo') {
                    eq.idEquipmentType = InternalEquipmentTypeEnum.EQUIPMENTS.id;
                } else if (typesEq != null && typesEq.trim().toLowerCase() === 'balanza') {
                    eq.idEquipmentType = InternalEquipmentTypeEnum.BALANCES.id;
                } else if (typesEq != null && typesEq.trim().toLowerCase() === 'masa') {
                    eq.idEquipmentType = InternalEquipmentTypeEnum.MASS.id;
                }
            }

            let idVars: number[] = [];
            const variablesSelected = this.getDataFromCell(row, 10)?.split(',').map(a => a.trim());

            if (!ArrayUtils.isEmpty(variablesSelected)) {
                idVars = variablesSelected.map(vari => mapVariables.get(vari)).filter(a => a != null);
            }
            eq.idsVariables = idVars;

            let columnNumber = 11;

            eq.responsibleEquipment = this.getDataFromCell(row, columnNumber);
            columnNumber += 1;
            const noticationR = this.getDataFromCell(row, columnNumber);
            eq.notificationResposibleEq = noticationR != null && noticationR?.toLowerCase()?.includes('x') ? true : false;
            columnNumber += 1;
            eq.observations = this.getDataFromCell(row, columnNumber);
            
            columnNumber += 1;
             if (!isInternal) {
                eq.clientName = this.getDataFromCell(row, columnNumber);
                columnNumber += 1;
            }
            const status = this.getDataFromCell(row, columnNumber);
            if (status != null && status.trim().toLowerCase() === 'fuera de uso') {
                eq.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
            } else if (status != null && status.trim().toLowerCase() === 'pendiente de certificado') {
                eq.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
            } else {
                eq.idStatus = InternalEquipmentStatusEnum.PENDING_TO_COMPLETE
            }
            columnNumber += 1;
        
            const idFolders: number[] = [];
            const folders = this.getDataFromCell(row, columnNumber)?.toLowerCase().split(',').map(a => a.trim());
            if (!ArrayUtils.isEmpty(folders)) {
                folders.forEach(app => {
                    const idFold = listFolders.find(d => d.name.toLowerCase() === app?.toLowerCase());
                    if (idFold) {
                        idFolders.push(idFold.id);
                    }
                });
                columnNumber += 1;
            }
            eq.idsFolder = idFolders;

            for (let index = columnNumber; index < information[0]?.length; index++) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const element = information[0][index];

                const resultCustom = listCustomFields.find(e => e.name.toLowerCase() === element?.toLowerCase());

                if (resultCustom) {
                    const value = this.getDataFromCell(row, index);
                    const custom = new InternalEquipmentCustomFieldValue();
                    custom.idCustomField = resultCustom.id;
                    custom.name = resultCustom.name;
                    custom.idType = resultCustom.idType;
                    if (value?.toLowerCase() === 'x') {
                        custom.value = 'true';
                    } else {
                        custom.value = value;
                    }

                    if (!eq.customFields) {
                        eq.customFields = [];
                    }
                    eq.customFields.push(custom);
                }
            }

            if (eq.name == null) {
                errors.push(`El identificador es nulo para la fila ${columnNumber}`);
            } else if (eq.idEquipmentType == null || !typesEq) {
                errors.push(`El tipo de equipo es nulo para la fila ${columnNumber}`);
            } else if (eq.idStatus == null) {
                errors.push(`El estado del equipo es nula para la fila ${columnNumber}`);
            }

            if (errors != null  && errors?.length > 0) {
                errors.forEach(e => {
                    console.log(e);
                });
                return;
            }

            const calibrationData = data.get(translate.instant('internalEq.sheetName.calibration') as string) as any[];
            const rowNumCalData = i + 1;
            const rowCalData = calibrationData.find(e => e[0] ===  this.getDataFromCell(row, 0)) as any;

            if (rowCalData) {
                const eqCalData = new InternalEquipmentPerformances();
                eqCalData.name = this.getDataFromCell(rowCalData, 0);
                eqCalData.idActionType = EquipmentPerformances.CALIBRATION;
                eqCalData.equipmentRange = this.getDataFromCell(rowCalData, 1);
                eqCalData.useRange = this.getDataFromCell(rowCalData, 2);
                eqCalData.resolution = this.getDataFromCell(rowCalData, 3);
                eqCalData.tolerance = this.getDataFromCell(rowCalData, 4);
                eqCalData.frequency = this.getDataFromCell(rowCalData, 5);
                
                const responsible = this.getDataFromCell(rowCalData, 6);
                if (responsible != null && responsible.trim().toLowerCase() === 'interno') {
                    eqCalData.responsible = CalibrationResponsibleEnum.INTERNAL;
                } else if (responsible != null && responsible.trim().toLowerCase() === 'externo') {
                    eqCalData.responsible = CalibrationResponsibleEnum.EXTERNAL;
                }

                eqCalData.supplier = this.getDataFromCell(rowCalData, 7);
                eqCalData.fee = this.getNumberFromCell(rowCalData, 8);

                const cDate = this.getDateFromCell(rowCalData, 9);
                if (cDate != null) {
                    const resultcDate = DateUtils.anyToDateWitouhTime(cDate);
                    eqCalData.processDate = resultcDate;
                }
                const eDate = this.getDateFromCell(rowCalData, 10);
                if (eDate != null) {
                    const resulteDate = DateUtils.anyToDateWitouhTime(eDate);
                    eqCalData.expirationDate = resulteDate;
                }

                eqCalData.documentNumber = this.getDataFromCell(rowCalData, 11);

                eqCalData.expansionDay = this.getNumberFromCell(rowCalData, 12);
                const hasExpDay = (eqCalData.expansionDay && eqCalData.expansionDay > 0);
                if (hasExpDay) {
                    const result = new Date(eqCalData.expirationDate);
                    result.setDate(result.getDate() + eqCalData.expansionDay);
                    eqCalData.extendedExpirationDate = DateUtils.anyToDateWitouhTime(result);
                }

                const status = this.getDataFromCell(row, 14);
                if (status != null && status.trim().toLowerCase() === 'en uso') {
                    eqCalData.idStatus = InternalEquipmentStatusEnum.EN_USO;
                } else if (status != null && status.trim().toLowerCase() === 'fuera de uso') {
                    eqCalData.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
                } else if (status != null && status.trim().toLowerCase() === 'caducado') {
                    eqCalData.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
                } else if ((status != null && status.trim().toLowerCase() === 'pendiente de certificado')) {
                    eqCalData.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                } else if (status != null && status.trim().toLowerCase() === 'extendido' || hasExpDay) {
                    eqCalData.idStatus = InternalEquipmentStatusEnum.EXTENDIDO;
                } else {
                    if (StringUtils.isNotEmpty(eqCalData.documentNumber)) {
                        eqCalData.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                    } else {
                        eqCalData.idStatus = InternalEquipmentStatusEnum.PENDING_TO_COMPLETE;
                    }
                }

                if (eqCalData.idStatus !== InternalEquipmentStatusEnum.EXTENDIDO && hasExpDay) {
                    eqCalData.idStatus = InternalEquipmentStatusEnum.EXTENDIDO;
                }

                if (eqCalData.name == null) {
                    errors.push(`El identificador es nulo para la fila ${rowNumCalData}`);
                } else if (eqCalData.responsible == null) {
                    errors.push(`El responsable de calibración es nulo para la fila ${rowNumCalData}`);
                } else if (eqCalData.processDate == null) {
                    errors.push(`La fecha de calibración es nula para la fila ${rowNumCalData}`);
                } else if (eqCalData.expirationDate == null) {
                    errors.push(`La fecha próxima de la calibración es nula para la fila ${rowNumCalData}`);
                }  else {
                    performanances.push(eqCalData);
                }
            }

            const verificationData = data.get(translate.instant('internalEq.sheetName.verification') as string) as any[];
            const rowNumvData = i + 1;
            const rowvData = verificationData.find(e => e[0] ===  this.getDataFromCell(row, 0)) as any;

            if (rowvData) {
                const eqv = new InternalEquipmentPerformances();
                eqv.name = this.getDataFromCell(rowvData, 0);
                eqv.idActionType = EquipmentPerformances.VERIFICATION;

                eqv.equipmentRange = this.getDataFromCell(rowvData, 1);
                eqv.verificationPoint = this.getDataFromCell(rowvData, 2);
                eqv.resolution = this.getDataFromCell(rowvData, 3);
                eqv.tolerance = this.getDataFromCell(rowvData, 4);
                eqv.frequency = this.getDataFromCell(rowvData, 5);
                const responsible = this.getDataFromCell(rowvData, 6);
                if (responsible != null && responsible.trim().toLowerCase() === 'interno') {
                    eqv.responsible = CalibrationResponsibleEnum.INTERNAL;
                } else if (responsible != null && responsible.trim().toLowerCase() === 'externo') {
                    eqv.responsible = CalibrationResponsibleEnum.EXTERNAL;
                }

                eqv.supplier = this.getDataFromCell(rowvData, 7);
                eqv.fee = this.getNumberFromCell(rowvData, 8);

                const aProcessDate = this.getDateFromCell(rowvData, 9);
                if (aProcessDate != null) {
                    const resultaDate = DateUtils.anyToDateWitouhTime(aProcessDate);
                    eqv.processDate = resultaDate;
                }
                const aExpirationDate = this.getDateFromCell(rowvData, 10);
                if (aExpirationDate != null) {
                    const resultaDate = DateUtils.anyToDateWitouhTime(aExpirationDate);
                    eqv.expirationDate = resultaDate;
                }

                eqv.documentNumber = this.getDataFromCell(rowvData, 11);

                const status = this.getDataFromCell(row, 12);
                if (status != null && status.trim().toLowerCase() === 'en uso') {
                    eqv.idStatus = InternalEquipmentStatusEnum.EN_USO;
                } else if (status != null && status.trim().toLowerCase() === 'fuera de uso') {
                    eqv.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
                } else if (status != null && status.trim().toLowerCase() === 'caducado') {
                    eqv.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
                } else if (status != null && status.trim().toLowerCase() === 'pendiente de certificado') {
                    eqv.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                }else {
                    if (StringUtils.isNotEmpty(eqv.documentNumber)) {
                        eqv.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                    } else {
                        eqv.idStatus = InternalEquipmentStatusEnum.PENDING_TO_COMPLETE;
                    }
                }

                if (eqv.name == null) {
                    errors.push(`El identificador es nulo para la fila ${rowNumvData}`);
                } else if (eqv.responsible == null) {
                    errors.push(`El responsable de verificación es nulo para la fila ${rowNumvData}`);
                } else if (eqv.processDate == null) {
                    errors.push(`La fecha de la verificación es nula para la fila ${rowNumvData}`);
                } else if (eqv.expirationDate == null) {
                    errors.push(`La fecha de la próxima verificación es nula para la fila ${rowNumvData}`);
                }  else {
                    performanances.push(eqv);
                }
            }

            const maintenanceData = data.get(translate.instant('internalEq.sheetName.maintenance') as string) as any[];
            const rowmData = maintenanceData.find(m => m[0] === this.getDataFromCell(row, 0)) as any;

            if (rowmData) {
                const eqv = new InternalEquipmentPerformances();
                eqv.name = this.getDataFromCell(rowmData, 0);
                eqv.idActionType = EquipmentPerformances.MAINTENANCE;

                const aProcessDate = this.getDateFromCell(rowmData, 1);
                if (aProcessDate != null) {
                    const resultaDate = DateUtils.anyToDateWitouhTime(aProcessDate);
                    eqv.processDate = resultaDate;
                }
                const aExpirationDate = this.getDateFromCell(rowmData, 2);
                if (aExpirationDate != null) {
                    const resultaDate = DateUtils.anyToDateWitouhTime(aExpirationDate);
                    eqv.expirationDate = resultaDate;
                }
                eqv.documentNumber = this.getDataFromCell(rowmData, 3);

                const status = this.getDataFromCell(row, 4);
                if (status != null && status.trim().toLowerCase() === 'en uso') {
                    eqv.idStatus = InternalEquipmentStatusEnum.EN_USO;
                } else if (status != null && status.trim().toLowerCase() === 'fuera de uso') {
                    eqv.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
                } else if (status != null && status.trim().toLowerCase() === 'caducado') {
                    eqv.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
                } else if (status != null && status.trim().toLowerCase() === 'pendiente de certificado') {
                    eqv.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                }else {
                    if (StringUtils.isNotEmpty(eqv.documentNumber)) {
                        eqv.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                    } else {
                        eqv.idStatus = InternalEquipmentStatusEnum.PENDING_TO_COMPLETE;
                    }
                }


                if (eqv.name == null) {
                    errors.push(`El identificador es nulo para la fila ${rowNumvData}`);
                } else if (eqv.processDate == null) {
                    errors.push(`La fecha de mantenimiento es nula para la fila ${rowNumvData}`);
                }else if (eqv.expirationDate == null) {
                    errors.push(`La fecha del próximo mantenimiento es nula para la fila ${rowNumvData}`);
                } else {
                    performanances.push(eqv);
                }
            }

            const qualificationData = data.get(translate.instant('internalEq.sheetName.qualification') as string) as any[];
            const rowqData = qualificationData.find(e => e[0] ===  this.getDataFromCell(row, 0)) as any;

            if (rowqData) {
                const eqv = new InternalEquipmentPerformances();
                eqv.name = this.getDataFromCell(rowqData, 0);
                eqv.idActionType = EquipmentPerformances.QUALIFICATION;

                const aProcessDate = this.getDateFromCell(rowqData, 1);
                if (aProcessDate != null) {
                    const resultaDate = DateUtils.anyToDateWitouhTime(aProcessDate);
                    eqv.processDate = resultaDate;
                }
                const aExpirationDate = this.getDateFromCell(rowqData, 2);
                if (aExpirationDate != null) {
                    const resultaDate = DateUtils.anyToDateWitouhTime(aExpirationDate);
                    eqv.expirationDate = resultaDate;
                }
                eqv.documentNumber = this.getDataFromCell(rowqData, 3);
                eqv.executionNumber = this.getDataFromCell(rowqData, 4);

                const status = this.getDataFromCell(row, 5);
                if (status != null && status.trim().toLowerCase() === 'en uso') {
                    eqv.idStatus = InternalEquipmentStatusEnum.EN_USO;
                } else if (status != null && status.trim().toLowerCase() === 'fuera de uso') {
                    eqv.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
                } else if (status != null && status.trim().toLowerCase() === 'caducado') {
                    eqv.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
                } else if (status != null && status.trim().toLowerCase() === 'pendiente de certificado') {
                    eqv.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                }else {
                    if (StringUtils.isNotEmpty(eqv.documentNumber)) {
                        eqv.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                    } else {
                        eqv.idStatus = InternalEquipmentStatusEnum.PENDING_TO_COMPLETE;
                    }
                }

                if (eqv.name == null) {
                    errors.push(`El identificador es nulo para la fila ${rowNumvData}`);
                } else if (eqv.processDate == null) {
                    errors.push(`La fecha de cualificación es nulo para la fila ${rowNumvData}`);
                } else if (eqv.expirationDate == null) {
                    errors.push(`La fecha próxima de cualificación es nulo para la fila ${rowNumvData}`);
                } else {
                    performanances.push(eqv);
                }
            }

            const characterizationnData = data.get(translate.instant('internalEq.sheetName.characterization') as string) as any[];
            const rowchData = characterizationnData.find(e => e[0] ===  this.getDataFromCell(row, 0)) as any;

            if (rowchData) {
                const eqv = new InternalEquipmentPerformances();
                eqv.idActionType = EquipmentPerformances.CHARACTERIZATION;
                eqv.name = this.getDataFromCell(rowchData, 0);

                const aProcessDate = this.getDateFromCell(rowchData, 1);
                if (aProcessDate != null) {
                    const resultaDate = DateUtils.anyToDateWitouhTime(aProcessDate);
                    eqv.processDate = resultaDate;
                }
                const aExpirationDate = this.getDateFromCell(rowchData, 2);
                if (aExpirationDate != null) {
                    const resultaDate = DateUtils.anyToDateWitouhTime(aExpirationDate);
                    eqv.expirationDate = resultaDate;
                }
                eqv.documentNumber = this.getDataFromCell(rowchData, 3);
                const status = this.getDataFromCell(row, 4);
                if (status != null && status.trim().toLowerCase() === 'en uso') {
                    eqv.idStatus = InternalEquipmentStatusEnum.EN_USO;
                } else if (status != null && status.trim().toLowerCase() === 'fuera de uso') {
                    eqv.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
                } else if (status != null && status.trim().toLowerCase() === 'caducado') {
                    eqv.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
                } else if (status != null && status.trim().toLowerCase() === 'pendiente de certificado') {
                    eqv.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                }else {
                    if (StringUtils.isNotEmpty(eqv.documentNumber)) {
                        eqv.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                    } else {
                        eqv.idStatus = InternalEquipmentStatusEnum.PENDING_TO_COMPLETE;
                    }
                }

                if (eqv.name == null) {
                    errors.push(`El identificador es nulo para la fila ${rowNumvData}`);
                } else if (eqv.processDate == null) {
                    errors.push(`La fecha de la caracterización es nula para la fila ${rowNumvData}`);
                } else if (eqv.expirationDate == null) {
                    errors.push(`La fecha próxima de caracterización es nula para la fila ${rowNumvData}`);
                } else {
                    performanances.push(eqv);
                }
            }

            
            if (errors != null  && errors?.length > 0) {
                errors.forEach(e => {
                    console.log(e);
                });
            } else {
                if (performanances.length > 0) {
                    if (!eq.performances) {
                        eq.performances =  [];
                    }
                    eq.performances = performanances;
                } 
                res.push(eq);
            }
        }
        return res;
    }

    private static async loadInternalEquipment(data: any[][], variableTypeService: VariableTypeService,
        translate: TranslateService):
        Promise<InternalEquipment[]> {
        const res: InternalEquipment[] = [];

        const mapVariables: Map<string, number> = new Map();
        const listVariables = await firstValueFrom(variableTypeService.findAll()) as Variable[];
        listVariables.forEach(v => mapVariables.set(translate.instant('variable.'.concat(v.translation)) as string, v.id));

        // Empezamos en 1 para saltarnos la primera fila, que es de cabeceras
        for (let i = 1; i < data.length; i++) {
            const rowNum = i + 1;
            const row = data[i];

            const eq = new InternalEquipment();
            eq.name = this.getDataFromCell(row, 0);
            eq.group = this.getDataFromCell(row, 1);
            eq.equipment = this.getDataFromCell(row, 2);
            eq.maker = this.getDataFromCell(row, 3);
            eq.model = this.getDataFromCell(row, 4);
            eq.serialNum = this.getDataFromCell(row, 5);

            const responsible = this.getDataFromCell(row, 6);
            if (responsible != null && responsible.trim().toLowerCase() === 'interno') {
                eq.responsible = CalibrationResponsibleEnum.INTERNAL;
            } else if (responsible != null && responsible.trim().toLowerCase() === 'externo') {
                eq.responsible = CalibrationResponsibleEnum.EXTERNAL;
            }

            eq.supplier = this.getDataFromCell(row, 7);
            eq.fee = this.getNumberFromCell(row, 8);

            const idApps: number[] = [];
            const applications = this.getDataFromCell(row, 9)?.toLowerCase().split(',').map(a => a.trim());
            if (!ArrayUtils.isEmpty(applications)) {
                applications.forEach(app => {
                    if (app === 'thermal') {
                        idApps.push(AppTypeEnum.THERMAL);
                    } else if (app === 'calibrates') {
                        idApps.push(AppTypeEnum.CALIBRATES);
                    } else if (app === 'air') {
                        idApps.push(AppTypeEnum.AIR);
                    }
                });
            }
            eq.idsApps = idApps;

            let idVars: number[] = [];
            const variablesSelected = this.getDataFromCell(row, 10)?.split(',').map(a => a.trim());

            if (!ArrayUtils.isEmpty(variablesSelected)) {
                idVars = variablesSelected.map(vari => mapVariables.get(vari)).filter(a => a != null);
            }
            eq.idsVariables = idVars;

            eq.equipmentRange = this.getDataFromCell(row, 11);
            eq.useRange = this.getDataFromCell(row, 12);
            eq.resolution = this.getDataFromCell(row, 13);
            eq.tolerance = this.getDataFromCell(row, 14);
            eq.calibrationFrequency = this.getDataFromCell(row, 15);
            eq.acquisitionDate = this.getDateFromCell(row, 16);

            eq.calibrateDate = this.getDateFromCell(row, 17);
            eq.expirationDate = this.getDateFromCell(row, 18);

            eq.certificateNum = this.getDataFromCell(row, 19);

            const status = this.getDataFromCell(row, 20);
            if (status != null && status.trim().toLowerCase() === 'en uso') {
                eq.idStatus = InternalEquipmentStatusEnum.EN_USO;
            } else if (status != null && status.trim().toLowerCase() === 'fuera de uso') {
                eq.idStatus = InternalEquipmentStatusEnum.FUERA_DE_USO;
            } else if (status != null && status.trim().toLowerCase() === 'caducado') {
                eq.idStatus = InternalEquipmentStatusEnum.CADUCADO;
            } else if (status != null && status.trim().toLowerCase() === 'extendido') {
                eq.idStatus = InternalEquipmentStatusEnum.EXTENDIDO;
                // eslint-disable-next-line max-len
            } else if (status != null && status.trim().toLowerCase() === 'pendiente de certificado') {
                eq.idStatus = InternalEquipmentStatusEnum.PENDING_CERTIFICATE;
                // eslint-disable-next-line max-len
            } else if (status != null && (status.trim().toLowerCase() === 'pendiente de revisión' || status.trim().toLowerCase() === 'pendiente de revision')) {
                eq.idStatus = InternalEquipmentStatusEnum.PENDING_REVIEW;
            }

            eq.observations = this.getDataFromCell(row, 21);

            if (eq.name == null) {
                console.error(`El identificador es nulo para la fila ${rowNum}`);
            } else if (eq.equipment == null) {
                console.error(`El tipo de equipo es nulo para la fila ${rowNum}`);
            } else if (eq.maker == null) {
                console.error(`El fabricante es nulo para la fila ${rowNum}`);
            } else if (eq.model == null) {
                console.error(`El modelo es nulo para la fila ${rowNum}`);
            } else if (eq.responsible == null) {
                console.error(`El responsable de calibración es nulo para la fila ${rowNum}`);
            } else if (eq.idStatus == null) {
                console.error(`El estado del equipo es nulo para la fila ${rowNum}`);
            } else {
                res.push(eq);
            }
        }

        return res;
    }

    private static loadResultParticles(data: any[][]): ResultExcelParticles {
        const res = new ResultExcelParticles();

        if (!((data[0][0] as string).startsWith('SOLAIR ') || !(data[0][0] as string).startsWith('ApexP3'))) {
            return res;
        } else if ((data[0][0] as string).startsWith('DateTime')) {
            return this.loadResultParticles1070(data);
        } else if ((data[0][0] as string).startsWith('Alarm')) {
            return this.loadResultParticlesSN(data);
        }

        const rawUnit = data[4][3] as string;
        const unit = rawUnit?.substring(rawUnit.indexOf('(') + 1, rawUnit.indexOf(')'));

        res.serialNum = data[1][0] as string;
        res.serialNum = res.serialNum?.replace(' ', '')?.replace(/\b\w+(?:\:)/, '')?.replace(' ', '');

        const colZeroOne = data[4].map((s: string) => s.trim()).findIndex(s => s === '0.1') || -1;
        const colZeroTwo = data[4].map((s: string) => s.trim()).findIndex(s => s === '0.2') || -1;
        const colZeroThree = data[4].map((s: string) => s.trim()).findIndex(s => s === '0.3') || -1;
        const colZeroFive = data[4].map((s: string) => s.trim()).findIndex(s => s === '0.5') || -1;
        const colOneZero = data[4].map((s: string) => s.trim()).findIndex(s => s === '1.0') || -1;
        // const colThreeZero = data[4].map((s: string) => s.trim()).findIndex(s => s === '3.0') || -1;
        const colFiveZero = data[4].map((s: string) => s.trim()).findIndex(s => s === '5.0') || -1;

        const colAlarm = data[4].map((s: string) => s.trim()).findIndex(s => s === 'Alarm') || -1;

        // Empezamos en 5 para saltarnos las primeras filas, que son de cabeceras
        for (let i = 5; i < data.length; i++) {
            const row = data[i];

            const dto = new EssayExecutionAirResultParticles();
            dto.date = this.getDateFromCell(row, 0);
            dto.location = this.getDataFromCell(row, 1);
            dto.samplingTime = this.getNumberFromCell(row, 2);
            dto.volume = this.getNumberFromCell(row, 3);
            dto.unit = unit;

            let factor = 1000 / dto.volume;

            if (unit?.toLowerCase() === 'ft3' || unit?.toLowerCase() === 'cfm') {
                factor = factor / 28.316846592;
            }

            dto.zeroOne = this.cellToParticles(row as string[], colZeroOne, factor);
            dto.zeroTwo = this.cellToParticles(row as string[], colZeroTwo, factor);
            dto.zeroThree = this.cellToParticles(row as string[], colZeroThree, factor);
            dto.zeroFive = this.cellToParticles(row as string[], colZeroFive, factor);
            dto.oneZero = this.cellToParticles(row as string[], colOneZero, factor);
            // dto.threeZero = this.cellToParticles(row as string[], colThreeZero, factor);
            dto.fiveZero = this.cellToParticles(row as string[], colFiveZero, factor);

            if (colAlarm > -1) {
                dto.alarm = this.cellToData(row as string[], colAlarm);
                dto.flow = this.cellToData(row as string[], colAlarm + 1);
                dto.laser = this.cellToData(row as string[], colAlarm + 2);
                dto.service = this.cellToData(row as string[], colAlarm + 3);
            }

            res.particles.push(dto);
        }

        return res;
    }

    private static loadResultParticles1070(data: any[][]): ResultExcelParticles {
        const res = new ResultExcelParticles();
        const colZeroOne = data[0].map((s: string) => s.trim()).findIndex(s => s === '>0.1') || -1;
        const colZeroTwo = data[0].map((s: string) => s.trim()).findIndex(s => s === '>0.2') || -1;
        const colZeroThree = data[0].map((s: string) => s.trim()).findIndex(s => s === '>0.3') || -1;
        const colZeroFive = data[0].map((s: string) => s.trim()).findIndex(s => s === '>0.5') || -1;
        const colOneZero = data[0].map((s: string) => s.trim()).findIndex(s => s === '>1.0') || -1;
        // const colThreeZero = data[4].map((s: string) => s.trim()).findIndex(s => s === '3.0') || -1;
        const colFiveZero = data[0].map((s: string) => s.trim()).findIndex(s => s === '>5.0') || -1;

        const colAlarm = data[0].map((s: string) => s.trim()).findIndex(s => s === 'Alarmed') || -1;

        // Empezamos en 1 para saltarnos la primera fila, que son de cabeceras
        for (let i = 1; i < data.length; i++) {
            const row = data[i];

            const dto = new EssayExecutionAirResultParticles();
            dto.date = this.getDateFromCell(row, 0);
            dto.location = this.getDataFromCell(row, 8);
            //dto.samplingTime = this.getNumberFromCell(row, 2);
            const volume = this.getDataFromCell(row, 10);
            const resVolume =  volume?.match(/\d+(\.\d+)?/g)?.join('') || null;
            if (resVolume) {
                dto.volume = Number(resVolume);
            }
            dto.unit =  volume?.replace(/[^a-zA-Z]+/g, '');;

            let factor = 1000 / dto.volume;

            if (dto.unit?.toLowerCase() === 'ft3' || dto.unit?.toLowerCase() === 'cfm' || dto.unit?.toLowerCase() === 'cf') {
                factor = factor / 28.316846592;
            }

            dto.zeroOne = this.cellToParticles(row as string[], colZeroOne, factor);
            dto.zeroTwo = this.cellToParticles(row as string[], colZeroTwo, factor);
            dto.zeroThree = this.cellToParticles(row as string[], colZeroThree, factor);
            dto.zeroFive = this.cellToParticles(row as string[], colZeroFive, factor);
            dto.oneZero = this.cellToParticles(row as string[], colOneZero, factor);
            // dto.threeZero = this.cellToParticles(row as string[], colThreeZero, factor);
            dto.fiveZero = this.cellToParticles(row as string[], colFiveZero, factor);

            if (colAlarm > -1) {
                dto.alarm = this.cellToData(row as string[], colAlarm);
                dto.flow = this.cellToData(row as string[], colAlarm + 1);
                dto.laser = this.cellToData(row as string[], colAlarm + 2);
                dto.service = this.cellToData(row as string[], colAlarm + 3);
            }

            res.particles.push(dto);
        }
        return res;
    }

    private static loadResultParticlesSN(data: any[][]): ResultExcelParticles {
        let resHeader;
        if (!data[0][1]) {
            resHeader = (data[0][0] as string).split("\\");
        } else {
            resHeader = (data[0]);
        }
        const res = new ResultExcelParticles();
        const colZeroOne = -1;
        const colZeroTwo = -1;
        const colZeroThree = resHeader.map((s: string) => s.trim()).findIndex(s => s === '0.3 Micron') || -1;
        const colZeroFive = resHeader.map((s: string) => s.trim()).findIndex(s => s === '0.5 Micron') || -1;
        const colOneZero = resHeader.map((s: string) => s.trim()).findIndex(s => s === '1.0 Micron') || -1;
        // const colThreeZero = data[4].map((s: string) => s.trim()).findIndex(s => s === '3.0') || -1;
        const colFiveZero = resHeader.map((s: string) => s.trim()).findIndex(s => s === '5.0 Micron') || -1;

        const colAlarm = 0;
        const colLaser = resHeader.map((s: string) => s.trim()).findIndex(s => s === 'Laser OK') || -1;
        const colFlow = resHeader.map((s: string) => s.trim()).findIndex(s => s === 'Flow CFM') || -1;

        // Empezamos en 1 para saltarnos la primera fila, que son de cabeceras
        for (let i = 1; i < data.length; i++) {
            const row = data[i];

            const dto = new EssayExecutionAirResultParticles();

            const dateString: string =   this.getDataFromCell(row, 1) + "," +  this.getDataFromCell(row, 2);
            const date = DateTime.fromFormat(dateString, "MM/dd/yy,HH:mm:ss");

            dto.date = date.toJSDate();
            dto.location = this.getDataFromCell(row, 3);
            const countMode = this.getDataFromCell(row, 9);
            const volume = this.getDataFromCell(row, 8);
            const resVolume =  volume?.match(/\d+(\.\d+)?/g)?.join('') || null;
            if (resVolume) {
                dto.volume = Number(resVolume);
            }
            dto.unit =  volume?.replace(/[^a-zA-Z]+/g, '');;

            let factor: number;
            if (countMode?.toLowerCase()?.includes('cm')) {
                factor = 1;
            } else {
                factor = 1000 / dto.volume;

                if (dto.unit?.toLowerCase() === 'ft3' || dto.unit?.toLowerCase() === 'cfm' || dto.unit?.toLowerCase() === 'cf') {
                    factor = factor / 28.316846592;
                }
            }

            dto.zeroOne = this.cellToParticles(row as string[], colZeroOne, factor);
            dto.zeroTwo = this.cellToParticles(row as string[], colZeroTwo, factor);
            dto.zeroThree = this.cellToParticles(row as string[], colZeroThree, factor);
            dto.zeroFive = this.cellToParticles(row as string[], colZeroFive, factor);
            dto.oneZero = this.cellToParticles(row as string[], colOneZero, factor);
            dto.fiveZero = this.cellToParticles(row as string[], colFiveZero, factor);

            if (colAlarm > -1) {
                dto.alarm = this.cellToData(row as string[], colAlarm);
                const resFlow = this.cellToData(row as string[], colFlow);
                dto.flow = resFlow ? resFlow + 'CFM' : resFlow;
                dto.laser = this.cellToData(row as string[], colLaser);
                //dto.service = this.cellToData(row as string[], colAlarm + 3);
            }

            res.particles.push(dto);
        }
        return res;
    }

    private static loadClient(data: any[][]): Client[] {
        const res: Client[] = [];

        // Empezamos en 1 para saltarnos la primera fila, que es de cabeceras
        for (let i = 1; i < data.length; i++) {
            const row = data[i];

            const eq = new Client();
            eq.name = this.getDataFromCell(row, 0);
            eq.address = this.getDataFromCell(row, 1);
            eq.postalCode = this.getDataFromCell(row, 2);
            eq.city = this.getDataFromCell(row, 3);
            eq.state = this.getDataFromCell(row, 4);
            eq.country = this.getDataFromCell(row, 5);
            eq.code = this.getDataFromCell(row, 6);

            res.push(eq);
        }

        return res;
    }

    private static getDataFromCell(row: any[], index: number): string {
        let res: string = null;

        try {
            res = row[index] as string;
        } catch (ex) {
            res = null;
        }

        return res;
    }

    private static getNumberFromCell(row: any[], index: number): number {
        let res: number = null;

        try {
            res = row[index] as number;
        } catch (ex) {
            res = null;
        }

        return res;
    }

    private static getDateFromCell(row: any[], index: number): Date {
        let res: Date = null;
        const cell = row[index] as string;

        let colDay = 0;
        const colMonth = 1;
        let colYear = 2;

        try {
            // Si tiene el formato normal de fecha (dd/MM/yyyy), calculamos la fecha con ese formato,
            // Si por el contrario es un número, se trata del formato especial de Excel.
            if (cell.toString().includes('/')) {
                const isDateTime = cell.toString().includes(':');

                const dateStr = isDateTime ? cell.split(' ')[0] : cell;

                const dateSplit: string[] = dateStr.split('/');

                // Obtenemos los campos individuales para todas las partes de la fecha
                let dd = +dateSplit[colDay];

                if (dd > 100) {
                    colDay = 2;
                    colYear = 0;

                    dd = +dateSplit[colDay];
                }

                const mm = +dateSplit[colMonth] - 1;
                const yyyy = +dateSplit[colYear];

                // Creamos la fecha con Javascript
                if (isDateTime) {
                    const hourSplit: string[] = cell.split(' ')[1].split(':');

                    const hh = hourSplit[0] ? +hourSplit[0] : 0;
                    const min = hourSplit[1] ? +hourSplit[1] : 0;
                    const sec = hourSplit[2] ? +hourSplit[2] : 0;

                    res = new Date(yyyy, mm, dd, hh, min, sec);
                } else {
                    res = new Date(yyyy, mm, dd);
                }

            } else if (cell.toString().includes('-')) {
                debugger;
                const isDateTime = cell.toString().includes(':');

                const dateStr = isDateTime ? cell.split(' ')[0] : cell;

                const dateSplit: string[] = dateStr.split('-');

                // Obtenemos los campos individuales para todas las partes de la fecha
                let dd = +dateSplit[colDay];

                if (dd > 100) {
                    colDay = 2;
                    colYear = 0;

                    dd = +dateSplit[colDay];
                }

                const mm = +dateSplit[colMonth] - 1;
                const yyyy = +dateSplit[colYear];

                // Creamos la fecha con Javascript
                if (isDateTime) {
                    const hourSplit: string[] = cell.split(' ')[1].split(':');

                    const hh = hourSplit[0] ? +hourSplit[0] : 0;
                    const min = hourSplit[1] ? +hourSplit[1] : 0;
                    const sec = hourSplit[2] ? +hourSplit[2] : 0;

                    res = new Date(yyyy, mm, dd, hh, min, sec);
                } else {
                    res = new Date(yyyy, mm, dd);
                }
            } else if (!isNaN(+cell)) {
                // Convertimos el dato Excel a fecha normal
                res = new Date(((+cell - (25567 + 1)) * 86400 * 1000) - 86400000);
            }
        } catch (ex) {
            res = null;
        }

        return res;
    }

    // allColumns (QLOD-290): en el formato generico (fecha, hora y una columna con cabecera por sonda)
    // leer TODAS las columnas con su cabecera, sin recortar a las primeras totalSensors. El recorte
    // obligaba a emparejar por posicion; quien pide allColumns empareja despues con excelSensorMatching
    // y falla si no puede hacerlo sin ambiguedad. Los formatos sin cabeceras siguen como estaban.
    private static loadSensorData(data: any[][], idVariable: number = null, multiple = false, totalSensors?: number,
        allColumns = false): SensorData[] {
        const limit = multiple ? totalSensors : 1;
        if (this.isRotronic(data)) {
            if (multiple) {
                throw new Error('No es posible la subida masiva en Rotronic');
            } else {
                return this.loadRotronic(data);
            }
        } else if (this.isEscort(data)) {
            if (multiple) {
                return this.loadEscort(data, idVariable, limit);
            } else {
                return this.loadEscort(data, idVariable, limit);
            }
        } else if (this.isLethality(data)) {
            if (multiple) {
                return this.loadLethality(data, idVariable, limit);
            } else {
                return this.loadLethality(data, idVariable, limit);
            }
        } else if (this.isCO2(data)) {
            if (multiple) {
                return this.loadCO2(data, idVariable, limit);
            } else {
                return this.loadCO2(data, idVariable, limit);
            }
        } else if (this.isCO2_v2(data) || this.isCO2_v3(data)) {
            if (multiple) {
                return this.loadCO2_v2(data, idVariable, limit);
            } else {
                return this.loadCO2_v2(data, idVariable, limit);
            }
        } else if (this.isGeneric(data)) {
            if (multiple) {
                return this.loadGeneric(data, idVariable, allColumns ? null : limit, allColumns);
            } else {
                return this.loadGeneric(data, idVariable, allColumns ? null : limit, allColumns);
            }
        } else if (this.isMedinsaCsv(data)) {
            if (multiple) {
                throw new Error('No es posible la subida masiva en Medinsa');
            } else {
                return this.loadMedinsaCsv(data, idVariable);
            }
        } else if (this.isMedinsaXls(data)) {
            if (multiple) {
                throw new Error('No es posible la subida masiva en Medinsa');
            } else {
                return this.loadMedinsaXls(data, idVariable);
            }
        } else if (this.isItaiXls(data)) {
            if (multiple) {
                throw new Error('No es posible la subida masiva en ITAI');
            } else {
                return this.loadItaiXls(data, idVariable);
            }
        } else if (this.isTesto(data)) {
            if (multiple) {
                throw new Error('No es posible la subida masiva en Testo');
            } else {
                return this.loadTesto(data, idVariable);
            }
        } else {
            throw new Error('Formato desconocido');
        }
    }

    private static isRotronic(data: any[][]): boolean {
        return data[1][0] === 'Rotronic';
    }

    private static isEscort(data: any[][]): boolean {
        return data[0][0] === ' ndice' && data[0][1] === 'Tiempo  Transcurrido' && data[0][2] === 'Fecha';
    }

    private static isCO2(data: any[][]): boolean {
        return data[0][2] === 'CO2, %';
    }

    private static isCO2_v2(data: any[][]): boolean {
        return data[0][1] === 'CO2, %';
    }

    private static isCO2_v3(data: any[][]): boolean {
        return data[0][0] === 'CO2, %';
    }

    private static isLethality(data: any[][]): boolean {
        let res = data[0][0] === 'Fecha' && data[0][1] === 'Hora';

        if (res) {
            let s1 = data[0][2];
            let s2 = data[0][3];

            if (s1 != null && s2 != null) {
                if (typeof s1 !== 'string') {
                    s1 = s1.toString();
                }
                if (typeof s2 !== 'string') {
                    s2 = s2.toString();
                }

                s1 = s1.split(' ');
                s2 = s2.split(' ');

                const cond1 = s1[0] === s2[0];

                const cond2 = s1[2] === 'Temperatura(°C)';
                const cond3 = s2[2] === 'Fo(min.)';

                res = cond1 && cond2 && cond3;
            } else {
                res = false;
            }
        }

        return res;
    }

    private static isMedinsaCsv(data: any[][]): boolean {
        return data[0].join(';').toLowerCase() === 'ID;Date;Time;Channel 1[%rH];Channel 2[°C]'.toLowerCase();
    }

    private static isMedinsaXls(data: any[][]): boolean {
        return data[1].join(';').toLowerCase() === 'id;Date/time;Channel 1[%rH];Channel 2[Â°C]'.toLowerCase();
    }

    private static isItaiXls(data: any[][]): boolean {
        return data[2].join(' ').toLowerCase() === 'File created by software: tempbase'.toLowerCase();
    }

    private static isTesto(data: any[][]): boolean {
        return data[0][0] === 'Nombre del instrumento: ' && (data[1][0] as string).startsWith('Hora de inicio:');
    }

    private static isDataTrace(data: any[][]): boolean {
        return data[0][0] === 'Número de Serie:' && data[0][1] === 'ID de Prueba:';
    }

    private static isFluke(data: any[][]): boolean {
        return data[0][0] === 'Record #' && data[0][1] === 'Time';
    }

    private static isXLOG(data: any[][]): boolean {
        return data[3][0] === 'Fecha &amp;amp; Hora' && data[3][1] === 'Acción';
    }

    private static isTmiOrion(data: any[][]): boolean {
        let res = data[0][0] === 'Fecha' && data[0][1] === 'Hora';

        if (res) {
            let s1: string | string[] = data[0][2] as string;
            let s2: string | string[] = data[0][3] as string;

            if (s1 != null && s2 != null) {
                s1 = s1.split(' ');
                s2 = s2.split(' ');

                res = s1 === s2;
            }
        }

        return res;
    }

    private static isPirani1(data: any[][]): boolean {
        return data[1][0] === 'fecha' && data[1][1] === 'Tiempo' && data[1][3] === 'Presión';
    }

    private static isPirani2(data: any[][]): boolean {
        return data[1][0] === 'Date' && data[1][1] === 'Time' && data[1][3] === 'Pressure';
    }

    private static isGeneric(data: any[][]): boolean {
        const firstCell = (data[0][0] as string) || '';
        const secondCell = (data[0][1] as string) || '';

        const dateFields = ['fecha', 'date'];
        const timeFields = ['hora', 'time'];

        return dateFields.includes(firstCell.toLowerCase().trim()) && timeFields.includes(secondCell.toLowerCase().trim());
    }

    private static loadRotronic(data: any[][]): SensorData[] {
        const res: SensorData[] = [];
        let indexCols = -1;
        let indexData = -1;

        for (let i = 0; i < data.length; i++) {

            const firstCol = data[i][0] as string;

            // En el if filtramos que ya esté asignado porque solo queremos el primero
            // Sumamos 1 en la asignación porque nos interesa la siguiente fila
            if (indexCols === -1 && firstCol === '[#H]') {
                indexCols = i + 1;
            } else if (indexData === -1 && firstCol === '[#D]Medidas') {
                indexData = i + 1;
            } else if (indexCols > -1 && indexData > -1) {
                // Si ya tenemos ambos datos, cortamos el bucle para no perder tiempo
                break;
            }
        }

        let indexTemp = -1;
        let indexHum = -1;

        if (indexCols > -1) {
            for (let i = 0; i < data[indexCols].length; i++) {
                const cell = data[indexCols][i] as string;

                if (cell === 'Temperatura') {
                    indexTemp = i;
                }

                if (cell === 'Humedad') {
                    indexHum = i;
                }

                // En este bucle no es necesario cortar, ya que tendrá pocos datos
            }
        }

        if (indexData > -1) {
            for (let i = indexData; i < data.length; i++) {
                const row = data[i];

                let dateOnly: string | Date | number = row[0] as string;

                if (typeof dateOnly === 'object') {
                    dateOnly = this.dateToDateStr(dateOnly);
                } else if (!isNaN(+dateOnly)) {
                    dateOnly = this.excelNumberToDateStr(+dateOnly);
                }

                const date = this.excelDatesToDate(dateOnly, row[1] as string, i);

                if (date != null && indexTemp > -1) {
                    const dataTemp = new SensorData();
                    dataTemp.idVariable = VariableTypeEnum.TEMPERATURE;
                    dataTemp.date = date;

                    let value: number | string = row[indexTemp] as string;

                    if (isNaN(+value)) {
                        value = value.replace(',', '.');
                        value = (+value);
                    }

                    dataTemp.value = NumberUtils.fixPrecision(value);

                    res.push(dataTemp);
                }

                if (date != null && indexHum > -1) {
                    const dataHum = new SensorData();
                    dataHum.idVariable = VariableTypeEnum.HUMIDITY;
                    dataHum.date = date;
                    let value: number | string = row[indexHum] as string;

                    if (isNaN(+value)) {
                        value = value.replace(',', '.');
                        value = (+value);
                    }

                    dataHum.value = NumberUtils.fixPrecision(value);

                    res.push(dataHum);
                }
            }
        }

        return res;
    }

    private static loadEscort(data: any[][], idVariable: number, totalSensors: number = null): SensorData[] {
        const res: SensorData[] = [];

        // Siempre tendremos 5 columnas por fichero, por lo que si
        // dividimos el total de columnas entre 5, tendremos el número de sensores.
        let totalSensorsInFile = Math.floor(data[0].length / 5);

        if (totalSensors != null) {
            totalSensorsInFile = Math.min(totalSensorsInFile, totalSensors);
        }

        // Empezamos a contar en la segunda fila, ya que la primera es de cabeceras
        for (let i = 1; i < data.length; i++) {
            const row = data[i];

            for (let j = 0; j < totalSensorsInFile; j++) {
                const sensorData = new SensorData();

                const baseCol = j * 5;

                let dateOnly: string | Date | number = row[baseCol + 2] as string;
                let timeOnly: string | Date | number = row[baseCol + 3] as string;

                if (Object.prototype.toString.call(dateOnly) === '[object Date]') {
                    dateOnly = this.dateToDateStr(DateUtils.anyToDate(dateOnly));
                } else if (!isNaN(+dateOnly)) {
                    dateOnly = this.excelNumberToDateStr(+dateOnly);
                }

                if (dateOnly) {
                    dateOnly = dateOnly.trim();
                }

                if (Object.prototype.toString.call(timeOnly) === '[object Date]') {
                    timeOnly = this.dateToHourStr(DateUtils.anyToDate(timeOnly));
                } else if (!isNaN(+timeOnly)) {
                    timeOnly = this.excelNumberToTimeStr(+timeOnly);
                }

                if (timeOnly) {
                    timeOnly = timeOnly.trim();
                }

                const dateStr = `${dateOnly} ${timeOnly}`;
                const date = this.stringToDate(dateStr);

                if (date != null && !isNaN(date.getTime())) {
                    sensorData.idVariable = idVariable;
                    sensorData.posEquipment = j;
                    sensorData.date = date;

                    let value: number | string = row[baseCol + 4] as string;

                    if (typeof value === 'string') {
                        value = value.replace(',', '.');
                        value = (+value);
                    }

                    sensorData.value = NumberUtils.fixPrecision(value);

                    res.push(sensorData);
                }

            }
        }

        return res;
    }

    private static loadLethality(data: any[][], idVariable: number, totalSensors: number = null): SensorData[] {
        const res: SensorData[] = [];

        // Siempre tendremos 5 columnas por fichero, por lo que si
        // dividimos el total de columnas entre 5, tendremos el número de sensores.
        let totalSensorsInFile = Math.floor((data[0].length - 2) / 2);

        if (totalSensors != null) {
            totalSensorsInFile = Math.min(totalSensorsInFile, totalSensors);
        }

        // Empezamos a contar en la segunda fila, ya que la primera es de cabeceras
        for (let i = 1; i < data.length; i++) {
            const row = data[i];

            const dateOnly: string | Date | number = row[0] as string;
            const timeOnly: string | Date | number = row[1] as string;

            const date = this.excelDatesToDate(dateOnly, timeOnly, i);

            for (let j = 0; j < totalSensorsInFile && date != null && !isNaN(date.getTime()); j++) {
                const sensorData = new SensorData();

                sensorData.idVariable = idVariable;
                sensorData.posEquipment = j;
                sensorData.date = date;

                let index = j * 2 + 2;
                if (idVariable === VariableTypeEnum.LETHALITY) {
                    index++;
                }
                let value: number | string = row[index] as string;

                if (typeof value === 'string') {
                    value = value.replace(',', '.');
                    value = (+value);
                }

                sensorData.value = NumberUtils.fixPrecision(value);

                res.push(sensorData);
            }
        }

        return res;
    }

    private static loadGeneric(data: any[][], idVariable: number, totalSensors: number = null, withHeaders = false): SensorData[] {
        const res: SensorData[] = [];

        // Siempre tendremos 2 columnas fijas, por lo que restaremos 2 al número de columnas del documento.
        let totalSensorsInFile = data[0].length - 2;

        if (totalSensors != null) {
            totalSensorsInFile = Math.min(totalSensorsInFile, totalSensors);
        }

        // Con cabeceras (QLOD-290) se ignoran las columnas sin cabecera y sin ningun dato: Excel guarda
        // columnas vacias al final y aqui una celda vacia se convierte en 0.
        const isEmptyCell = (v: unknown) => v == null || (typeof v === 'string' && StringUtils.isEmpty(v.trim()));
        const skippedColumns = new Set<number>(withHeaders ?
            Array.from({ length: Math.max(totalSensorsInFile, 0) }, (_, j) => j)
                .filter(j => isEmptyCell(data[0][j + 2]) && data.slice(1).every(r => isEmptyCell(r[j + 2]))) : []);
        const skipColumn = (j: number) => skippedColumns.has(j);

        // Empezamos a contar en la segunda fila, ya que la primera es de cabeceras
        for (let i = 1; i < data.length; i++) {
            const row = data[i];

            const dateOnly = row[0] as string;
            const timeOnly = row[1] as string;

            if ((typeof dateOnly === 'string' && StringUtils.isEmpty(dateOnly)) ||
                (typeof timeOnly === 'string' && StringUtils.isEmpty(timeOnly))) {
                continue;
            }

            const date = this.excelDatesToDate(dateOnly, timeOnly, i);

            for (let j = 0; j < totalSensorsInFile && date != null && !isNaN(date.getTime()); j++) {
                if (skipColumn(j)) {
                    continue;
                }

                const sensorData = new SensorData();

                sensorData.idVariable = idVariable;
                sensorData.posEquipment = j;
                sensorData.date = date;

                let value: number | string = row[j + 2] as string;

                if (typeof value === 'string') {
                    value = value.replace(',', '.');
                    value = (+value);
                }

                sensorData.value = NumberUtils.fixPrecision(value);

                if (withHeaders || totalSensors > 1) {
                    // Validate With Regex _
                    sensorData.equipmentName = data[0][j + 2] as string;
                    sensorData.serialNum = data[0][j + 2] as string;

                    if (StringUtils.isNotEmpty(sensorData.equipmentName)) {
                        sensorData.equipmentName = sensorData.equipmentName.toString().trim();
                    }

                    if (StringUtils.isNotEmpty(sensorData.serialNum)) {
                        sensorData.serialNum = sensorData.serialNum.toString().trim();
                    }
                }

                res.push(sensorData);
            }
        }

        return res;
    }

    private static loadCO2(data: any[][], idVariable: number, totalSensors: number = null) {
        return this.loadCO2Generic(data, idVariable, totalSensors, 1);
    }

    private static loadCO2_v2(data: any[][], idVariable: number, totalSensors: number = null) {
        return this.loadCO2Generic(data, idVariable, totalSensors, 0);
    }

    private static loadCO2Generic(data: any[][], idVariable: number, totalSensors: number = null, offset: number = 2): SensorData[] {
        const res: SensorData[] = [];

        // Siempre tendremos 2 columnas fijas, por lo que restaremos 2 al número de columnas del documento.
        // En algunos casos raros esta resta puede dar -1, así que ponemos que al menos haya
        let totalSensorsInFile = Math.max(data[0].length - 2, 1);

        if (totalSensors != null) {
            totalSensorsInFile = Math.min(totalSensorsInFile, totalSensors);
        }

        // Empezamos a contar en la segunda fila, ya que la primera es de cabeceras
        for (let i = 1; i < data.length; i++) {
            const row = data[i];

            let dateStr: Date | string = row[offset] as string;

            if (Object.prototype.toString.call(dateStr) === '[object Date]') {
                dateStr = this.dateTimeToHourStr(DateUtils.anyToDate(dateStr));
            } else if (!isNaN(+dateStr)) {
                dateStr = this.excelNumberToDateTimeStr(+dateStr);
            }

            if (dateStr) {
                dateStr = dateStr.replace('  ', ' ').trim();
            }

            const date = this.stringToDate(dateStr);

            if (date != null && !isNaN(date.getTime())) {

                for (let j = 0; j < totalSensorsInFile; j++) {
                    const sensorData = new SensorData();

                    sensorData.idVariable = idVariable;
                    sensorData.posEquipment = j;
                    sensorData.date = date;

                    let value: string | number = row[j + offset + 1] as string;

                    if (typeof value === 'string') {
                        value = +(value.replace(',', '.'));
                    }

                    sensorData.value = NumberUtils.fixPrecision(value);

                    res.push(sensorData);
                }
            }
        }

        return res;
    }

    private static loadMedinsaCsv(data: any[][], idVariable: number): SensorData[] {
        const res: SensorData[] = [];

        const dataColumn: number = VariableTypeEnum.HUMIDITY === idVariable ? 2 : 3;

        for (const row of data) {

            if (ArrayUtils.isEmpty(row) || isNaN(+row[0])) {
                continue;
            }

            let dateStr: Date | string = row[1] as string;
            let value: number | string = row[dataColumn] as string;

            if (Object.prototype.toString.call(dateStr) === '[object Date]') {
                dateStr = this.dateTimeToHourStr(DateUtils.anyToDate(dateStr));
            } else if (!isNaN(+dateStr)) {
                dateStr = this.excelNumberToDateTimeStr(+dateStr);
            }

            if (dateStr) {
                dateStr = dateStr.replace('  ', ' ').trim();
            }
            const date = this.stringToDate(dateStr);

            if (value != null) {
                value = value.toString().replace(',', '.');
                if (!isNaN(+value)) {
                    value = (+value);
                }
            } else {
                continue;
            }

            const sensorData = new SensorData();
            sensorData.value = NumberUtils.fixPrecision(value);
            sensorData.date = date;
            sensorData.idVariable = idVariable;

            res.push(sensorData);
        }

        return res;
    }

    private static loadMedinsaXls(data: any[][], idVariable: number): SensorData[] {
        const res: SensorData[] = [];

        const dataColumn: number = VariableTypeEnum.HUMIDITY === idVariable ? 2 : 3;

        for (const row of data) {

            if (ArrayUtils.isEmpty(row) || isNaN(+row[0])) {
                continue;
            }

            let dateOnly: string | Date | number = row[1] as string;
            let timeOnly: string | Date | number = row[2] as string;
            let value: number | string = row[dataColumn] as string;

            if (Object.prototype.toString.call(dateOnly) === '[object Date]') {
                dateOnly = this.dateToDateStr(DateUtils.anyToDate(dateOnly));
            } else if (!isNaN(+dateOnly)) {
                dateOnly = this.excelNumberToDateStr(+dateOnly);
            }

            if (dateOnly) {
                dateOnly = dateOnly.trim();
            }

            if (Object.prototype.toString.call(timeOnly) === '[object Date]') {
                timeOnly = this.dateToHourStr(DateUtils.anyToDate(timeOnly));
            } else if (!isNaN(+timeOnly)) {
                timeOnly = this.excelNumberToTimeStr(+timeOnly);
            }

            if (timeOnly) {
                timeOnly = timeOnly.trim();
            }

            const dateStr = `${dateOnly} ${timeOnly}`;
            const date = this.stringToDate(dateStr);

            if (value != null) {
                value = value.toString().replace(',', '.');
                if (!isNaN(+value)) {
                    value = (+value);
                }
            } else {
                continue;
            }

            const sensorData = new SensorData();
            sensorData.value = NumberUtils.fixPrecision(value);
            sensorData.date = date;
            sensorData.idVariable = idVariable;

            res.push(sensorData);
        }

        return res;
    }

    private static loadItaiXls(data: any[][], idVariable: number): SensorData[] {
        const res: SensorData[] = [];
        let indexData = -1;

        for (let i = 0; i < data.length; i++) {

            const row = data[i].join(';');

            // En el if filtramos que ya esté asignado porque solo queremos el primero
            // Sumamos 1 en la asignación porque nos interesa la siguiente fila
            if (indexData === -1 && row.toLowerCase().startsWith('ID;Time;Temp'.toLowerCase())) {
                indexData = i + 1;
            } else if (indexData > -1) {
                // Si ya tenemos ambos datos, cortamos el bucle para no perder tiempo
                break;
            }
        }

        if (indexData > -1) {
            for (let i = indexData; i < data.length; i++) {
                const row = data[i];

                const date = this.stringToDate(row[1] as string);

                if (date != null) {
                    const dataTemp = new SensorData();
                    dataTemp.idVariable = idVariable;
                    dataTemp.date = date;

                    let value: number | string = row[2] as string;

                    if (isNaN(+value)) {
                        value = value.replace(',', '.');
                        value = (+value);
                    }

                    dataTemp.value = NumberUtils.fixPrecision(value);

                    res.push(dataTemp);
                }
            }
        }

        return res;
    }

    private static loadTesto(data: any[][], idVariable: number): SensorData[] {
        const res: SensorData[] = [];
        let indexData = -1;

        for (let i = 0; i < data.length; i++) {

            const firstCol = data[i][0] as string;
            const secondCol = data[i][1] as string;

            // En el if filtramos que ya esté asignado porque solo queremos el primero
            // Sumamos 1 en la asignación porque nos interesa la siguiente fila
            if (firstCol === 'id' && secondCol === 'Fecha-Hora') {
                indexData = i + 1;
                break;
            }
        }

        if (indexData > -1) {
            for (let i = indexData; i < data.length; i++) {
                const row = data[i];

                let dateStr: Date | string = row[1] as string;

                if (Object.prototype.toString.call(dateStr) === '[object Date]') {
                    dateStr = this.dateTimeToHourStr(DateUtils.anyToDate(dateStr));
                } else if (!isNaN(+dateStr)) {
                    dateStr = this.excelNumberToDateTimeStr(+dateStr);
                }

                if (dateStr) {
                    dateStr = dateStr.replace('  ', ' ').trim();
                }

                const date = this.stringToDate(dateStr);

                if (date != null) {
                    const dataTemp = new SensorData();
                    dataTemp.idVariable = idVariable;
                    dataTemp.date = date;

                    let value: number | string = row[2] as string;

                    if (isNaN(+value)) {
                        value = value.replace(',', '.');
                        value = (+value);
                    }

                    dataTemp.value = NumberUtils.fixPrecision(value);

                    res.push(dataTemp);
                }
            }
        }

        return res;
    }

    private static excelDatesToDate(dateOnly: Date | number | string, timeOnly: Date | number | string, rowIndex: number): Date {

        if (Object.prototype.toString.call(dateOnly) === '[object Date]') {
            // Caso tipo Date
            dateOnly = this.dateToDateStr(DateUtils.anyToDate(dateOnly));
        } else if (!isNaN(+dateOnly)) {
            // Caso número de Excel
            dateOnly = this.excelNumberToDateStr(+dateOnly);
        } else if (typeof dateOnly === 'string') {
            // Caso string
            dateOnly = this.excelStringToDateStr(dateOnly);
        }

        let dateOnlyStr: string = null;

        if (dateOnly) {
            dateOnlyStr = dateOnly.toString().trim();
        } else {
            return null;
        }

        if (Object.prototype.toString.call(timeOnly) === '[object Date]') {
            timeOnly = this.dateToHourStr(DateUtils.anyToDate(timeOnly));
        } else if (!isNaN(+timeOnly)) {
            timeOnly = this.excelNumberToTimeStr(+timeOnly);
        } else if (typeof timeOnly === 'string') {
            // Caso string
            timeOnly = this.excelStringToTimeStr(timeOnly);
        }

        let timeOnlyStr: string = null;

        if (timeOnly) {
            timeOnlyStr = timeOnly.toString().trim();
        } else {
            return null;
        }

        const dateStr = `${dateOnlyStr} ${timeOnlyStr}`;
        let date = this.parseFlexibleDate(dateStr, rowIndex);

        if (isNaN(date.getTime())) {
            console.error(`No se ha reconocido la fecha ${dateOnlyStr}, hora ${timeOnlyStr}. Fila: ${(rowIndex + 1)}`);
            date = null;
        }

        return date;
    }

    private static stringToDate(str: string): Date {
        let date = DateTime.fromFormat(str, 'dd/LL/yyyy HH:mm:ss').toJSDate();

        if (isNaN(date.getTime())) {
            const dt = DateTime.fromFormat(str, 'dd/LL/yyyy HH:mm:ss ZZZ');
            if (dt.isValid) {
                const date = dt.toJSDate();
            } else {
                console.error('Fecha inválida:', dt.invalidExplanation);
            }
        }

        if (isNaN(date.getTime())) {
            date = DateTime.fromFormat(str, 'LL/dd/yyyy HH:mm:ss').toJSDate();
        }

        if (isNaN(date.getTime())) {
            date = DateTime.fromFormat(str, 'LL/dd/yyyy HH:mm:ss ZZZ').toJSDate();
        }

        if (isNaN(date.getTime())) {
            date = DateTime.fromFormat(str, 'dd-LLL-yyyy HH:mm:ss').toJSDate();
        }

        if (isNaN(date.getTime())) {
            date = null;
        }

        return date;
    }

    private static excelNumberToDateStr(num: number): string {
        const rawDate: Date = new Date((num - 25569) * 86400 * 1000);

        const date: Date = new Date(rawDate.toDateString());

        return this.dateToDateStr(date);
    }

    private static excelStringToDateStr(str: string): string {
        let isDDMM = false;
        try {
            isDDMM = isDDMM || DateTime.fromFormat(str, 'dd/LL/yyyy').isValid;
            isDDMM = isDDMM || DateTime.fromFormat(str, 'dd/LL/yy').isValid;
        } catch (e) {

        }

        const data = str.split('/');

        if (data.length < 2) {
            return null;
        }

        let day = String(data[isDDMM ? 0 : 1]);
        let month = String(data[isDDMM ? 1 : 0]);
        let year = String(data[2]);

        if (month.length < 2) {
            month = '0' + month;
        }
        if (day.length < 2) {
            day = '0' + day;
        }
        if (year.length < 3) {
            year = '20' + year;
        }

        return `${month}/${day}/${year}`;
    }

    private static excelNumberToDateTimeStr(num: number): string {
        const date = getJsDateFromExcel(num);
        return this.dateTimeToHourStr(new Date(date));
    }

    private static excelNumberToTimeStr(num: number): string {
        num = num * daysToSec;

        const date = new Date(1900, 0, 1);
        date.setSeconds(date.getSeconds() + num);

        return this.dateToHourStr(date);
    }

    private static dateToDateStr(date: Date): string {
        let month = String(date.getMonth() + 1);
        let day = String(date.getDate());
        const year = String(date.getFullYear());

        if (month.length < 2) {
            month = '0' + month;
        }
        if (day.length < 2) {
            day = '0' + day;
        }

        return `${day}/${month}/${year}`;
    }

    private static dateToHourStr(date: Date): string {
        let hour = `${date.getHours()}`;
        let minutes = `${date.getMinutes()}`;
        let seconds = `${date.getSeconds()}`;

        if (hour.length < 2) {
            hour = '0' + hour;
        }
        if (minutes.length < 2) {
            minutes = '0' + minutes;
        }
        if (seconds.length < 2) {
            seconds = '0' + seconds;
        }

        return `${hour}:${minutes}:${seconds}`;
    }

    private static excelStringToTimeStr(str: string): string {
        str = str.replace(' AM', '');

        const isAM = str.includes(' PM') && !str.startsWith('12:');

        str = str.replace(' PM', '');

        const data = str.split(':');

        if (data.length < 2) {
            return null;
        }

        let hour = data[0];
        let minutes = data[1];
        let seconds = data[2];

        if (isAM) {
            hour = ((+hour) + 12).toString();
        }

        if (hour.includes('24')) {
            hour = '00';
        }

        if (hour.length < 2) {
            hour = '0' + hour;
        }
        if (minutes.length < 2) {
            minutes = '0' + minutes;
        }
        if (seconds.length < 2) {
            seconds = '0' + seconds;
        }

        return `${hour}:${minutes}:${seconds}`;
    }

    private static dateTimeToHourStr(date: Date): string {
        return this.dateToDateStr(date) + ' ' + this.dateToHourStr(date);
    }

    private static cellToParticles(row: string[], column: number, factor: number): number {
        if (column < 0) {
            return null;
        }

        return Math.round(this.getNumberFromCell(row, column) * factor);
    }

    private static cellToData(row: string[], column: number): string {
        if (column < 0) {
            return null;
        }

        return this.getDataFromCell(row, column);
    }

    static async excelToParticleCountValidate(file: File, attachmentThermalService: AttachmentThermalService, translateService: TranslateService):
        Promise<string> {
        const res = await this.convertToCsv(file, attachmentThermalService);
        const data = res.file;
        const raw = res.raw;
        const dataExcel = await this.loadExcel(data, raw);

        if (!dataExcel.some(lines => lines.length > 1)) {
             for (let i = 0; i < dataExcel.length; i++) {
                const line = dataExcel[i].toString();
                dataExcel[i] = line.split('.');
            }
        }

        return this.loadResultParticlesValidate(dataExcel,translateService);
    }

    private static loadResultParticlesValidate(data: any[][], translateService: TranslateService): string  {
        const res = new ResultExcelParticles();

        if (((data[0][0] as string).startsWith('SOLAIR ') || (data[0][0] as string).startsWith('ApexP3'))) {
            for (let i = 5; i < data.length; i++) {
                const row = data[i];
    
                const dto = new EssayExecutionAirResultParticles();
                const location= this.getDataFromCell(row, 1);
                const result= res.particles.find(loc => loc.location === location);
                if (!result){
                    dto.location = this.getDataFromCell(row, 1);
                    res.particles.push(dto);
                } else{
                    return translateService.instant('essayAir.excel.duplication')
                }
            }
        } else if ((data[0][0] as string).startsWith('DateTime')) {
            for (let i = 1; i < data.length; i++) {
                const row = data[i];
    
                const dto = new EssayExecutionAirResultParticles();
                const location= this.getDataFromCell(row, 8);
                const result= res.particles.find(loc => loc.location === location);
                if (!result){
                    dto.location = this.getDataFromCell(row,8);
                    res.particles.push(dto);
                } else{
                    return translateService.instant('essayAir.excel.duplication')
                }
            }
        } else if ((data[0][0] as string).startsWith('Alarm')) {
            for (let i = 1; i < data.length; i++) {
                const row = data[i];
    
                const dto = new EssayExecutionAirResultParticles();
                const location= this.getDataFromCell(row, 3);
                const result= res.particles.find(loc => loc.location === location);
                if (!result){
                    dto.location = this.getDataFromCell(row, 3);
                    res.particles.push(dto);
                } else{
                    return translateService.instant('essayAir.excel.duplication')
                }
            }
        }       
        return null;
    }

    private static stringToWbMoreSheet(str: string, raw: boolean): Map<string, any[]> {
        let data: any[][];
        const dataSheet = new Map<string, any[]>();
        const bstr: string = str;
        const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary', raw });
        let count = 0;
        while (count < 6) {
            const wsname: string = wb.SheetNames[count];
            const ws: XLSX.WorkSheet = wb.Sheets[wsname];

            const options: XLSX.Sheet2JSONOpts = { header: 1, raw: true };

            /* save data */
            data = (XLSX.utils.sheet_to_json(ws, options));

            data = data.map((s: any[]) => {
                if (s.length === 1 && typeof s[0] === 'string') {
                    const dat = s[0];
                    s = dat.split(';');
                }

                return s as any[][];
            });
            count += 1;

            dataSheet.set(wsname, data);
        }
        return dataSheet;
    }

    private static loadExcelMoreSheet(file: File, raw: boolean): Promise<Map<string, any[]>> {
        return new Promise((resolve, reject) => {

            if (typeof file === 'string') {
                resolve(this.stringToWbMoreSheet(file, raw));
            } else {
                const reader: FileReader = new FileReader();

                reader.onload = (e: any) => resolve(this.stringToWbMoreSheet(e.target.result as string, raw));

                reader.onerror = (error: any) => reject(error);

                reader.readAsBinaryString(file);
            }
        });
    }


    private static loadResultParticlesCalibration(data: any[][]): CalibrationResultExcelParticles {
        const res = new CalibrationResultExcelParticles();

        if (!((data[0][0] as string).startsWith('SOLAIR ') || !(data[0][0] as string).startsWith('ApexP3'))) {
            return res;
        }

        res.serialNum = data[1][0] as string;
        res.serialNum = res.serialNum?.replace(' ', '')?.replace(/\b\w+(?:\:)/, '')?.replace(' ', '');

        const rawUnit = data[4][3] as string;
        const unit = rawUnit?.substring(rawUnit.indexOf('(') + 1, rawUnit.indexOf(')'));

        const colZeroOne = data[4].map((s: string) => s.trim()).findIndex(s => s === '0.1') || -1;
        const colZeroTwo = data[4].map((s: string) => s.trim()).findIndex(s => s === '0.2') || -1;
        const colZeroThree = data[4].map((s: string) => s.trim()).findIndex(s => s === '0.3') || -1;
        const colZeroFive = data[4].map((s: string) => s.trim()).findIndex(s => s === '0.5') || -1;
        const colOneZero = data[4].map((s: string) => s.trim()).findIndex(s => s === '1.0') || -1;
        // const colThreeZero = data[4].map((s: string) => s.trim()).findIndex(s => s === '3.0') || -1;
        const colFiveZero = data[4].map((s: string) => s.trim()).findIndex(s => s === '5.0') || -1;

        const colAlarm = data[4].map((s: string) => s.trim()).findIndex(s => s === 'Alarm') || -1;

        // Empezamos en 5 para saltarnos las primeras filas, que son de cabeceras
        for (let i = 5; i < data.length; i++) {
            const row = data[i];

            const dto = new CalibrationAmbientResultParticles();
            dto.model = data[0][0] as string;
            dto.date = this.getDateFromCell(row, 0);
            dto.location = this.getDataFromCell(row, 1);
            dto.samplingTime = this.getNumberFromCell(row, 2);
            dto.volume = this.getNumberFromCell(row, 3);
            dto.unit = unit;

            let factor = 1000 / dto.volume;

            if (unit?.toLowerCase() === 'ft3' || unit?.toLowerCase() === 'cfm') {
                factor = factor / 28.316846592;
            }

            dto.zeroOne = this.getNumberFromCell(row as string[], colZeroOne);
            dto.zeroTwo = this.getNumberFromCell(row as string[], colZeroTwo);
            dto.zeroThree = this.getNumberFromCell(row as string[], colZeroThree);
            dto.zeroFive = this.getNumberFromCell(row as string[], colZeroFive);
            dto.oneZero = this.getNumberFromCell(row as string[], colOneZero);
            // dto.threeZero = this.cellToParticles(row as string[], colThreeZero, factor);
            dto.fiveZero = this.getNumberFromCell(row as string[], colFiveZero);

            if (colAlarm > -1) {
                dto.alarm = this.cellToData(row as string[], colAlarm);
                dto.flow = this.cellToData(row as string[], colAlarm + 1);
                dto.laser = this.cellToData(row as string[], colAlarm + 2);
                dto.service = this.cellToData(row as string[], colAlarm + 3);
            }

            res.particles.push(dto);
        }

        return res;
    }

    private static async loadResultBalanceMass(data: any[][]): Promise<ExternalCalibrationValue[]> {
        let res: ExternalCalibrationValue[] = [];

        for (let i = 1; i < data.length; i++) {
            const row = data[i];
            
            const mass = this.getDataFromCell(row, 0);
            const massId = this.getDataFromCell(row, 1);
            const average = this.getDataFromCell(row, 2);
            const uncertainty = this.getDataFromCell(row, 3);
            const annotation = this.getDataFromCell(row, 4);

            const dto = new ExternalCalibrationValue();
            dto.point = NumberUtils.toNumberOrNull(mass);
            dto.pattern =  average;
            dto.average =  NumberUtils.toNumberOrNull(average);
            dto.uncertainty = NumberUtils.toNumberOrNull(uncertainty);
            dto.annotation = annotation === 'x';
            dto.idMass = massId;
             if (dto.point == null) {
                console.error(`El punto es nulo para la fila ${i}`);
            } else if (dto.average == null) {
                console.error(`La media es nulo para la fila ${i}`);
            } else if (dto.uncertainty == null) {
                console.error(`La incertidumbre es nulo para la fila ${i}`);
            } else {
                res.push(dto)
            }
            
        }

        return res;
    }

    private static parseFlexibleDate(dateStr: string, rowIndex: number): Date | null {
        for (const format of tryFormats) {
            const dt = DateTime.fromFormat(dateStr, format);
            if (dt.isValid) {
                return dt.toJSDate();
            }
        }

        console.error(`No se ha reconocido la fecha y hora: "${dateStr}". Fila: ${rowIndex + 1}`);
        return null;
    }

}
